import React from "react";
import { Check } from "lucide-react";
import { Section, Button, Card, Badge } from "@vild/site-ui";

const PricingTier = ({
  name,
  price,
  description,
  features,
  highlight = false,
  seats,
  teams,
  usage,
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlight?: boolean;
  seats: string;
  teams: string;
  usage: string;
}) => (
  <Card className={`flex flex-col h-full transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-violet-500/10 ${highlight ? "border-zinc-500 bg-gradient-to-br from-zinc-900 to-zinc-800/50 shadow-lg shadow-violet-500/20" : "bg-transparent hover:border-zinc-600/60"}`} hover={false}>
    <div className="mb-6">
      <h3 className="text-xl font-bold text-white mb-2 flex items-center justify-between">
        {name}
        {highlight && <Badge className="bg-gradient-to-r from-violet-600 to-violet-500 text-white border-transparent shadow-lg">Recommended</Badge>}
      </h3>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-mono font-bold text-transparent bg-clip-text bg-gradient-to-r from-zinc-200 to-zinc-300">{price}</span>
        <span className="text-zinc-500">/mo</span>
      </div>
      <p className="text-zinc-400 text-sm mt-2">{description}</p>
    </div>

    <div className="space-y-4 mb-8 border-y border-zinc-800 py-6">
      <div className="flex justify-between text-sm">
        <span className="text-zinc-400">Seats included</span>
        <span className="font-mono text-zinc-200">{seats}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-zinc-400">Teams</span>
        <span className="font-mono text-zinc-200">{teams}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-zinc-400">Usage rate</span>
        <span className="font-mono text-zinc-200">{usage}</span>
      </div>
    </div>

    <ul className="space-y-3 mb-8 flex-1">
      {features.map((feat) => (
        <li key={feat} className="flex items-start gap-3 text-sm text-zinc-300">
          <Check className="w-4 h-4 text-zinc-50 shrink-0 mt-0.5" />
          <span>{feat}</span>
        </li>
      ))}
    </ul>

    <Button variant={highlight ? "primary" : "outline"} className="w-full" asLink="/signup">
      Start Free
    </Button>
  </Card>
);

export const Pricing: React.FC = () => {
  return (
    <>
      <Section className="text-center pb-10">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white via-violet-200 to-violet-300">Transparent pricing.</h1>
        <p className="text-xl text-zinc-400">Start free. Scale when ready.</p>
      </Section>

      <Section className="pt-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <PricingTier
            name="Solo"
            price="$0"
            description="For hobbyists and freelancers."
            seats="1 seat ($10/add)"
            teams="1 team"
            usage="10% markup"
            features={["Unlimited pipelines", "All standard nodes", "API access", "Community support"]}
          />
          <PricingTier
            name="Business"
            price="$49"
            description="For growing teams and agencies."
            highlight={true}
            seats="15 seats ($6/add)"
            teams="3 teams"
            usage="5% markup"
            features={[
              "Everything in Solo",
              "Unlimited pipelines",
              "Priority support",
              "Shared library",
              "Increased rate limits",
            ]}
          />
          <PricingTier
            name="Enterprise"
            price="$499"
            description="For large organizations."
            seats="50 seats ($3/add)"
            teams="Unlimited"
            usage="2.5% markup"
            features={["Everything in Business", "Custom nodes", "SSO & Audit Logs", "99.9% Uptime SLA", "Concierge onboarding"]}
          />
        </div>

        <div className="mt-12 p-6 bg-gradient-to-r from-zinc-900/50 via-zinc-800/30 to-zinc-900/50 border border-zinc-700/50 rounded-lg text-center shadow-lg backdrop-blur-sm">
          <p className="text-zinc-300 font-medium">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-violet-300 font-bold">$10 free credits</span> are included with every new account. Test the runtime on us.
          </p>
        </div>
      </Section>

      <Section className="max-w-3xl">
        <h2 className="text-2xl font-bold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-400">Frequently Asked Questions</h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">What counts as a seat?</h3>
            <p className="text-zinc-400">
              A seat is any user who can edit pipelines or view organization activity. Read-only links for approval gates do not require a seat.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">How does usage pricing work?</h3>
            <p className="text-zinc-400">
              You pay for the compute time your pipelines consume. Different nodes have different rates (e.g. standard processing vs AI-powered tasks). You can see exact costs in the "Cost Tracking" feature.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Can I switch tiers?</h3>
            <p className="text-zinc-400">
              Yes, you can upgrade or downgrade at any time. Changes take effect at the start of the next billing cycle.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">What happens to starter credits?</h3>
            <p className="text-zinc-400">They never expire and are applied automatically to your first month's usage bill.</p>
          </div>
        </div>
      </Section>
    </>
  );
};

